"use client"

import { useSetAtom } from "jotai"
import { useEffect } from "react"

import { selectedBlockIdAtom, selectionAtom } from "@/stores/course-material/materialFeedbackStore"
import { courseMaterialBlockClass } from "@/utils/course-material/constants"

import { TEXT_SELECTION_TOOLTIP_ID } from "./TextSelectionTooltip"

export const FEEDBACK_DIALOG_CONTENT_ID = "feedback-dialog-content"
export const IGNORE_BLOCK_FEEDBACK_CLASS = "feedback-ignore-block"

const useSelectionTracking = (): void => {
  const setSelection = useSetAtom(selectionAtom)
  const setSelectedBlockId = useSetAtom(selectedBlockIdAtom)

  useEffect(() => {
    const abortController = new AbortController()

    /** Whether the node has been removed from the document, e.g. by a re-render mid-event. */
    function isDetached(node: Node | null): boolean {
      return node !== null && !node.isConnected
    }

    function isChildOfCourseMaterialBlock(node: Node | null | undefined): boolean {
      if (node === null || node === undefined) {
        return false
      }
      let element
      if (node.nodeType === Node.ELEMENT_NODE) {
        element = node as Element
      } else {
        // probably a text node
        element = node.parentElement
      }
      const closest = element?.closest(`.${courseMaterialBlockClass}`)
      return closest !== null && closest !== undefined
    }

    /** Tells whether the click or selection change is within a container within which we don't want to update the block ID */
    function isWithinIgnoredContainer(node: Node | null | undefined): boolean {
      if (node === null || node === undefined) {
        return false
      }
      let element
      if (node.nodeType === Node.ELEMENT_NODE) {
        element = node as Element
      } else {
        element = node.parentElement
      }
      const isWithinIgnoredBlock = parentHasCssSelector(element, `.${IGNORE_BLOCK_FEEDBACK_CLASS}`)
      const isWithinFeedback = parentHasCssSelector(
        element,
        // oxlint-disable-next-line i18next/no-literal-string
        `#${FEEDBACK_DIALOG_CONTENT_ID}, #${TEXT_SELECTION_TOOLTIP_ID}`,
      )
      return isWithinIgnoredBlock || isWithinFeedback
    }

    function parentHasCssSelector(el: Element | null, cssSelector: string): boolean {
      const content = el?.closest(cssSelector)
      return content !== null && content !== undefined
    }

    function selectedCourseBlocks(selection: Selection): boolean {
      const firstNode = selection.anchorNode
      const lastNode = selection.focusNode
      return (
        isChildOfCourseMaterialBlock(firstNode) &&
        isChildOfCourseMaterialBlock(lastNode) &&
        !isWithinIgnoredContainer(firstNode) &&
        !isWithinIgnoredContainer(lastNode)
      )
    }

    function selectionHandler(this: Document) {
      const selection = this.getSelection()
      // A detached endpoint says nothing about where the selection is, so keep what we have
      // instead of reading it as "nothing selected".
      if (selection && (isDetached(selection.anchorNode) || isDetached(selection.focusNode))) {
        return
      }
      if (selection && selectedCourseBlocks(selection)) {
        const newSelection = selection.toString()
        if (selection.rangeCount === 0) {
          return
        }
        const range = selection.getRangeAt(0)
        const rect = range.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const element = range.commonAncestorContainer.parentElement ?? undefined
        setSelection(
          newSelection,
          {
            x: centerX + window.scrollX,
            y: rect.top + window.scrollY,
          },
          element,
        )
      } else {
        setSelection("", undefined, undefined)
      }
    }

    function handleClick(ev: MouseEvent): void {
      // Skip updating block ID if text is selected
      const selectedText = window.getSelection()?.toString() || ""
      if (selectedText.trim().length > 0) {
        return
      }

      if (ev.target instanceof Element) {
        // react-aria's onPress runs before this bubbles up, so the handler it fired may already
        // have unmounted the target. A detached ancestor chain has no block to find, and clearing
        // the id here would undo the selection that press just made.
        if (isDetached(ev.target)) {
          return
        }

        // Skip if click is within feedback dialog
        if (isWithinIgnoredContainer(ev.target)) {
          return
        }

        let newBlockId = null
        let element: Element | null = ev.target
        while (element !== null) {
          if (element.classList.contains(courseMaterialBlockClass)) {
            newBlockId = element.id
            break
          }
          element = element.parentElement
        }
        setSelectedBlockId(newBlockId)
      } else {
        setSelectedBlockId(null)
      }
    }

    document.addEventListener("selectionchange", selectionHandler, {
      signal: abortController.signal,
    })
    document.addEventListener("click", handleClick, { signal: abortController.signal })

    return () => {
      abortController.abort()
    }
  }, [setSelection, setSelectedBlockId])
}

const SelectionListener: React.FC = () => {
  useSelectionTracking()
  return null
}

export default SelectionListener
